import React from 'react';
import history from '../../history';
import { buildStepList, findStepIndex, buildSidebarTree } from './steps.config';

class Shell extends React.Component {
  state = { leaveModalOpen: false };

  openLeaveModal = () => {
    // In sandbox mode the logo is just a logo - nothing to leave to.
    if (!this.props.applicationId) return;
    this.setState({ leaveModalOpen: true });
  };

  closeLeaveModal = () => this.setState({ leaveModalOpen: false });

  confirmLeave = () => {
    this.setState({ leaveModalOpen: false });
    history.push(`/dashboard/${this.props.applicationId}`);
  };

  renderStatusDot = ({ isActive, isCompleted }) => {
    return (
      <span
        className="inline-flex items-center justify-center h-5 w-5 rounded-full flex-shrink-0 text-[10px] font-bold"
        style={{
          backgroundColor: isCompleted ? '#7C5CFC' : isActive ? '#7C5CFC' : '#2A2D3D',
          color: isCompleted || isActive ? '#FFFFFF' : '#7E8299',
        }}
      >
        {isCompleted ? (
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : null}
      </span>
    );
  };

  renderPersonalSection = (entry) => {
    const titleColor = entry.isActive
      ? '#FFFFFF'
      : entry.isCompleted
        ? '#C7CAD6'
        : '#9CA0AE';

    return (
      <li key={entry.title} className="mb-2">
        <button
          type="button"
          onClick={() => this.props.onJump && this.props.onJump(entry.firstStepId)}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors"
          style={{
            backgroundColor: entry.isActive ? 'rgba(124, 92, 252, 0.15)' : 'transparent',
          }}
        >
          {this.renderStatusDot(entry)}
          <span className="text-sm font-semibold" style={{ color: titleColor }}>
            {entry.title}
          </span>
        </button>

        <ul className="ml-8 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {entry.applicants.map((a) => (
            <li key={a.stepId}>
              <button
                type="button"
                onClick={() => this.props.onJump && this.props.onJump(a.stepId)}
                className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: a.isActive ? '#7C5CFC' : a.isCompleted ? '#5B3FE0' : '#3A3F52',
                  }}
                />
                <span
                  className="text-[11px]"
                  style={{
                    color: a.isActive ? '#FFFFFF' : a.isCompleted ? '#C7CAD6' : '#6E7287',
                    fontWeight: a.isActive ? 600 : 400,
                  }}
                >
                  {a.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  renderJointSection = (entry) => {
    // Render the joint section's items as a simple list - each is its own
    // distinct step (Living expenses, Liabilities, Assets etc.) so they
    // remain individually clickable.
    return (
      <li key={entry.title} className="mb-3">
        <p className="text-[10px] uppercase tracking-widest text-[#7E8299] mb-2 font-bold px-3">
          {entry.title}
        </p>
        <ul className="space-y-0.5">
          {entry.items.map((item) => (
            <li key={item.stepId}>
              <button
                type="button"
                onClick={() => this.props.onJump && this.props.onJump(item.stepId)}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors"
                style={{
                  backgroundColor: item.isActive ? 'rgba(124, 92, 252, 0.15)' : 'transparent',
                }}
              >
                {this.renderStatusDot(item)}
                <span
                  className="text-sm font-medium"
                  style={{
                    color: item.isActive ? '#FFFFFF' : item.isCompleted ? '#C7CAD6' : '#9CA0AE',
                  }}
                >
                  {item.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  renderSidebar = (steps, currentStepId) => {
    const tree = buildSidebarTree(steps, currentStepId);

    // Split tree into personal section group and joint section groups.
    const personalEntries = tree.filter((t) => t.type === 'personal-section');
    const jointEntries = tree.filter((t) => t.type === 'joint-section');

    const { applicationId } = this.props;
    const logoImg = (
      <>
        <img
          src={process.env.PUBLIC_URL + '/images/logos/light_transparent.png'}
          alt="LoanGateway"
          className="h-8 w-auto"
        />
        <p className="text-xs text-[#B5B8C5] mt-2">Fact Find</p>
      </>
    );
    return (
      <aside
        className="hidden lg:flex flex-col w-80 flex-shrink-0 px-5 py-9 sticky top-0 h-screen"
        style={{ backgroundColor: '#1A1D2A' }}
      >
        <div className="mb-10 px-3">
          {applicationId ? (
            <button
              type="button"
              onClick={this.openLeaveModal}
              className="text-left rounded-md focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
              title="Back to dashboard"
              aria-label="Back to dashboard"
            >
              {logoImg}
            </button>
          ) : (
            logoImg
          )}
        </div>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {personalEntries.length > 0 ? (
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-widest text-[#7E8299] mb-2 font-bold px-3">
                About you
              </p>
              <ul>
                {personalEntries.map((entry) => this.renderPersonalSection(entry))}
              </ul>
            </div>
          ) : null}

          <ul>
            {jointEntries.map((entry) => this.renderJointSection(entry))}
          </ul>
        </div>

        <div className="text-[10px] text-[#5E6278] pt-6 border-t border-[#2A2D3D] mt-4 px-3">
          Test prototype - data is not saved
        </div>
      </aside>
    );
  };

  renderMobileHeader = (steps, currentIdx, currentStep) => {
    const pct = ((currentIdx + 1) / steps.length) * 100;
    const eyebrow = currentStep.isPersonal
      ? `${currentStep.group} - ${currentStep.label}`
      : currentStep.label;
    const { applicationId } = this.props;

    const logoImg = (
      <img
        src={process.env.PUBLIC_URL + '/images/logos/light_transparent.png'}
        alt="LoanGateway"
        className="h-5 w-auto flex-shrink-0"
      />
    );
    return (
      <header
        className="lg:hidden sticky top-0 z-20 px-3 pt-2 pb-1.5"
        style={{ backgroundColor: '#1A1D2A' }}
      >
        <div className="flex items-center justify-between gap-3">
          {applicationId ? (
            <button
              type="button"
              onClick={this.openLeaveModal}
              className="rounded-md focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
              title="Back to dashboard"
              aria-label="Back to dashboard"
            >
              {logoImg}
            </button>
          ) : (
            logoImg
          )}
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
            <span className="text-[11px] font-medium text-[#C7CAD6] truncate">
              {eyebrow}
            </span>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap"
              style={{ backgroundColor: 'rgba(124, 92, 252, 0.2)', color: '#C4B5FD' }}
            >
              {currentIdx + 1}/{steps.length}
            </span>
          </div>
        </div>
        <div className="h-1 bg-[#2A2D3D] rounded-full overflow-hidden mt-1.5">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: '#7C5CFC' }}
          />
        </div>
      </header>
    );
  };

  renderDesktopBreadcrumb = (steps, currentIdx, currentStep) => {
    const eyebrow = currentStep.isPersonal
      ? `${currentStep.group} - ${currentStep.label}`
      : currentStep.label;
    return (
      <div className="hidden lg:block sticky top-0 z-10 bg-[#F5F6FA]/95 backdrop-blur px-8 py-4 border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] font-semibold">
            {eyebrow} - Step {currentIdx + 1} of {steps.length}
          </div>
          <div className="flex items-center gap-1">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === currentIdx ? 20 : 8,
                  backgroundColor: i <= currentIdx ? '#7C5CFC' : '#E5E7EB',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  renderLeaveModal() {
    if (!this.state.leaveModalOpen) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
        style={{ backgroundColor: 'rgba(15, 17, 26, 0.55)', backdropFilter: 'blur(2px)' }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) this.closeLeaveModal();
        }}
      >
        <div
          className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
          style={{ border: '1px solid #E5E7EB' }}
        >
          <div className="px-5 sm:px-6 py-5 border-b border-[#E5E7EB]">
            <div
              className="inline-flex items-center justify-center h-10 w-10 rounded-full mb-3"
              style={{ backgroundColor: '#EFEAFE', color: '#5B3FE0' }}
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-[#1F2937]">
              Leave the fact find?
            </h2>
            <p className="mt-1 text-sm text-[#6B7280]">
              Your progress is saved automatically - you can pick up right where
              you left off whenever you come back.
            </p>
          </div>
          <div className="px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end rounded-b-2xl">
            <button
              type="button"
              onClick={this.closeLeaveModal}
              className="rounded-xl border-2 bg-white px-4 py-2.5 text-sm font-semibold"
              style={{ borderColor: '#D1D5DB', color: '#1F2937' }}
            >
              Keep going
            </button>
            <button
              type="button"
              onClick={this.confirmLeave}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-md"
              style={{ backgroundColor: '#7C5CFC' }}
            >
              Yes, go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { applicants, currentStepId, children } = this.props;
    const steps = buildStepList(applicants);
    const currentIdx = Math.max(0, findStepIndex(steps, currentStepId));
    const currentStep = steps[currentIdx] || steps[0];

    return (
      <div
        data-theme="loangateway"
        className="min-h-screen w-full"
        style={{ backgroundColor: '#F5F6FA' }}
      >
        <div className="flex">
          {this.renderSidebar(steps, currentStep.id)}

          <main className="flex-1 min-w-0">
            {this.renderMobileHeader(steps, currentIdx, currentStep)}
            {this.renderDesktopBreadcrumb(steps, currentIdx, currentStep)}

            <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
              <div className="max-w-3xl mx-auto">
                {children}
              </div>
            </div>

            <footer className="text-center text-xs text-[#6B7280] py-6 px-4">
              Copyright 2026 LoanGateway - Fact Find test prototype - data is not persisted
            </footer>
          </main>
        </div>
        {this.renderLeaveModal()}
      </div>
    );
  }
}

export default Shell;
